import type { LandingContent } from "./types";
import heroBoilers from "@/assets/services/boilers-heating-1280.webp";
import bAgeing from "@/assets/lp/boiler/boiler-card-ageing.webp";
import bBreakdown from "@/assets/lp/boiler/boiler-card-breakdown.webp";
import bControls from "@/assets/lp/boiler/boiler-card-controls.webp";
import bSludge from "@/assets/lp/boiler/boiler-card-sludge.webp";
import bUneven from "@/assets/lp/boiler/boiler-card-uneven.webp";
import bEvidence from "@/assets/lp/boiler/boiler-evidence.webp";
import bStep from "@/assets/lp/boiler/boiler-step-1.webp";

/**
 * Body content for the /boilers-heating page. Drives the shared
 * LpOrderedSections renderer (trust, testimonials, pricing, FAQ).
 *
 * Page-specific bands (inspect-first, what-we-see, what-we-do, process,
 * coordinated-upgrade, on-the-day, social-housing) are inlined in
 * `src/components/landing/lp-boilers-heating.tsx`.
 *
 * Repositioning rules: no "independent" / "no commission" language; no
 * £ savings figures; no finance offers (solar-only); no guarantee
 * durations (use manufacturer-backed warranty language).
 */
export const boilersHeatingLp: LandingContent = {
  pageKind: "boilers",
  slug: "boilers-heating",
  path: "/lp/boilers-heating",
  title: "Boilers & Heating In South Wales",
  metaTitle: "Boilers & Heating South Wales | Gas Safe | CRS Energy",
  metaDescription:
    "Boiler replacements and heating upgrades across South Wales. Gas Safe registered engineers, manufacturer-backed warranty, free assessment and property-specific quotes.",
  defaultService: "Boilers & Heating",
  heroHeadline: "Boilers & Heating In South Wales",
  heroSubheading:
    "Efficient, reliable heating across Wales and the South of England. Boiler replacements and heating upgrades by Gas Safe registered engineers, with manufacturer-backed warranties and clear, property-specific quotes.",
  heroBullets: [
    "Gas Safe Registered",
    "Manufacturer-Backed Warranty",
    "Free Assessment",
    "Property-Specific Quotes",
    "No Obligation",
  ],
  heroImage: heroBoilers,
  heroAlt: "Gas Safe registered engineer commissioning a wall-mounted gas boiler in a UK home",
  heroFloatImages: [
    { src: heroBoilers, slug: "b-hero" },
    { src: bAgeing, slug: "b-ageing" },
    { src: bBreakdown, slug: "b-breakdown" },
    { src: bControls, slug: "b-controls" },
    { src: bSludge, slug: "b-sludge" },
    { src: bUneven, slug: "b-uneven" },
    { src: bEvidence, slug: "b-evidence" },
    { src: bStep, slug: "b-step" },
  ],
  trustItems: [
    "Gas Safe Registered",
    "Manufacturer-Backed Warranty",
    "Free Assessment",
    "Property-Specific Quotes",
    "No Obligation",
  ],
  problemTitle: "Why It Matters",
  problemPoints: [
    {
      title: "Efficiency Through Correct Sizing",
      body: "An appropriately sized, modern boiler properly commissioned tends to run at higher efficiency than an oversized or ageing unit working harder than it needs to.",
    },
    {
      title: "Reliability You Can Plan Around",
      body: "Replacing an end-of-life boiler before it fails avoids reactive callouts and the discomfort of a cold home in the middle of winter.",
    },
    {
      title: "Comfort & Control",
      body: "Paired with up-to-date controls and balanced radiators, a new boiler delivers steadier room temperatures and easier day-to-day control.",
    },
  ],
  clarityTitle: "What A Good Boiler Install Looks Like",
  clarityBody:
    "Correct boiler type and output for the property, clean install with new isolation valves and a system filter, the system flushed and dosed, controls configured for how the home is used, commissioning records completed, manufacturer warranty registered and Gas Safe notification logged.",
  productTitle: "What We Do",
  productPoints: [
    {
      title: "Boiler Replacement",
      body: "Combi, system and regular boiler replacements from leading manufacturers, sized to the property and installed by a Gas Safe registered engineer.",
    },
    {
      title: "System Upgrades",
      body: "Radiator balancing, valve replacements, cylinder upgrades and pipework improvements to bring the wider heating system in line with the new boiler.",
    },
    {
      title: "Repairs",
      body: "Diagnosis and repair of existing gas boilers and heating faults, with clear advice on whether repair or replacement is the better option.",
    },
    {
      title: "Power Flush & System Protection",
      body: "Power flushing, inhibitor dosing and inline magnetic filter installation to protect the new boiler and improve heat distribution.",
    },
    {
      title: "Controls Upgrade",
      body: "Programmable and smart thermostats, TRVs and zoning, configured to suit your home. Paired with new or existing boilers.",
    },
  ],
  processSteps: [
    {
      step: 1,
      title: "Assessment",
      description:
        "We check your existing boiler, flue, gas supply, radiators and controls so the recommendation actually fits the property.",
      icon: "magnifier",
    },
    {
      step: 2,
      title: "Recommendation & Quote",
      description:
        "A written, property-specific quote covering boiler type, output, system protection, controls and any additional works.",
      icon: "file",
    },
    {
      step: 3,
      title: "Install & Commission",
      description:
        "Gas Safe registered engineer installs the new boiler, flushes and doses the system, and commissions to manufacturer requirements.",
      icon: "drill",
    },
    {
      step: 4,
      title: "Aftercare & Registration",
      description:
        "Gas Safe notification, Benchmark book completed and manufacturer warranty registered on your behalf, plus aftercare guidance.",
      icon: "certificate",
    },
  ],
  qualificationTitle: "What Affects The Recommendation",
  qualificationItems: [
    "Property size, layout and occupancy",
    "Existing system type (combi, system, regular)",
    "Hot water demand and number of bathrooms",
    "Gas supply, flue position and access",
    "Radiator condition and existing controls",
  ],
  includedItems: [
    "Free pre-installation assessment",
    "Manufacturer-supplied boiler and ancillaries",
    "Magnetic system filter and inhibitor",
    "Gas Safe install and commissioning",
    "Benchmark documentation",
    "Manufacturer warranty registration",
    "Aftercare guidance",
  ],
  trustSectionTitle: "Gas Safe Installs, Manufacturer-Backed Warranty",
  trustSectionBody:
    "A boiler install is a regulated, notifiable piece of work. We treat it as such: assessed before quoting, installed by a Gas Safe registered engineer, commissioned to manufacturer requirements and registered so the warranty stands.",
  benefits: [
    { title: "Correctly Sized Boiler", description: "Specified to the property rather than a one-size-fits-all swap." },
    { title: "System Protected", description: "Power flushed where needed, dosed and fitted with a magnetic filter." },
    { title: "Properly Commissioned", description: "Set to manufacturer requirements with records completed." },
    { title: "Manufacturer Warranty Registered", description: "Warranty registered on your behalf so cover is in place from day one." },
  ],
  testimonialsFull: [
    {
      name: "Adrian Cosgrove",
      date: "July 2026",
      serviceTag: "New Heating System",
      source: "trustpilot",
      quote:
        "I can thoroughly recommend CRS, they fitted a brand new heating system in my house. Nathan and his team were brilliant, the whole job was completed in a day, they cleaned up after themselves, were polite and courteous. Well done boys.",
    },
    {
      name: "C Morris",
      date: "May 2026",
      serviceTag: "New Boiler",
      source: "trustpilot",
      quote:
        "Company installed a new boiler for us last year 2025. Gavin and Steve were friendly, polite and professional, they explained everything to put our minds at rest. I would highly recommend this company, outstanding service.",
    },
    {
      name: "Jennifer W",
      date: "July 2025",
      serviceTag: "Boiler, Loft Insulation & Fans",
      source: "google",
      quote:
        "Had a very good experience with CRS, they were very helpful in the process of things and the workmen who did the job of boiler, attic insulation and fans were very professional, friendly and on time. Cleaned up after themselves with no mess left and were very helpful. Would definitely recommend this firm to anyone.",
    },
  ],
  faqs: [
    {
      question: "Which boiler is right for my home?",
      answer:
        "It depends on the property: size and layout, hot water demand, number of bathrooms, gas supply and existing system type. We assess all of that before recommending a combi, system or regular boiler and a sensible output. The recommendation is written down so you can see why it suits your home.",
    },
    {
      question: "Do I need a power flush?",
      answer:
        "Not always. We inspect the existing system, check for sludge and circulation issues, and recommend a power flush only when the system actually needs one. Where a flush is not required, we still dose with inhibitor and fit a magnetic filter to protect the new boiler.",
    },
    {
      question: "How long does a boiler swap take?",
      answer:
        "Most straightforward like-for-like swaps are completed in around a day. System changes, flue relocations or boiler-type changes can take longer. We confirm the realistic timeline in the written quote so there are no surprises.",
    },
    {
      question: "What warranty will I have?",
      answer:
        "Boilers we install carry a manufacturer-backed warranty, registered on your behalf when the work is commissioned. Specific warranty length depends on the manufacturer, model and installer accreditation, and is confirmed in the written quote.",
    },
    {
      question: "Should I consider a heat pump instead?",
      answer:
        "For some homes a heat pump is a good fit, for others a modern gas boiler is the more sensible choice today. We treat it as a genuine question rather than a sales angle. If you want to explore low-carbon heating, we can run a separate heat pump assessment, and you can read more on our Air Source Heat Pumps page.",
    },
    {
      question: "Do you offer landlord gas safety (LGSR / CP12) and HA / commercial work?",
      answer:
        "Yes. We carry out landlord gas safety inspections and certificates, planned boiler replacement programmes, communal and HA heating works, void works and provide compliance and completion documentation for landlords and managing agents.",
    },
  ],
  ctaTitle: "Book Your Free Boiler & Heating Assessment",
  sectionOrder: ["trust", "testimonials", "pricing", "faq"],
};
